import { PlusOutlined } from "@ant-design/icons";
import { Select } from "antd";
import { getIn } from "formik";
import { useMemo } from "react";
import { FormControlError } from "../../../../../components/atoms/FormFiledError";
import { MODULES_SELECTION_DATA_ENDPOINTS } from "../../../../../components/molecules/select/utils";
import { useSelectContext } from "../../../../../hooks/useSelectContext";
import { StyledSelectTableDiv } from "../../../../shared/orderLineForm/components/createPurchaseOrderPageV2.styles";

export const FormControlTableSearchableSelectAPI = ({
  name,
  moduleName,
  label = "",
  multiple = false,
  placeholder = "",
  required = false,
  activeRecords = false,
  readOnly = false,
  params,
  context = "general",
  handleOnChange = null,
  onCreate = null,
  createLabel = "Create",
  popupRender: userPopupRender,
  ...props
}) => {
  const {
    formik: { getFieldProps, touched, errors, setFieldValue },
  } = useSelectContext(context);

  const config = MODULES_SELECTION_DATA_ENDPOINTS[moduleName];

  if (!config) throw new Error(`Unknown module config: ${moduleName}`);

  const { data, isLoading } = config.func(STRING_PARAMS.includes(moduleName) ? params : { params });

  const options = useMemo(() => {
    const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    const fieldName = ["products", "equipment"].includes(moduleName) ? "number" : "name";

    const normalized = config.prepare
      ? config.prepare(list)
      : list.map(item => ({ label: item[fieldName], value: item.id }));
    return normalized;
  }, [config, data, moduleName]);

  const handleChange = (value, option) => {
    setFieldValue(name, value);

    if (handleOnChange) {
      handleOnChange(value, option);
    }
  };

  const { value, ...restFieldData } = getFieldProps(name);

  const popupRender =
    onCreate || userPopupRender
      ? menu => {
          const body = userPopupRender ? userPopupRender(menu) : menu;
          if (!onCreate) return body;
          return (
            <>
              <div
                role="button"
                tabIndex={0}
                style={{
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  color: "#1677ff",
                  borderBottom: "1px solid #e9edf5",
                }}
                onMouseDown={e => e.preventDefault()}
                onClick={() => onCreate()}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onCreate();
                  }
                }}
              >
                <PlusOutlined />
                <span>{createLabel}</span>
              </div>
              {body}
            </>
          );
        }
      : undefined;

  return (
    <>
      <StyledSelectTableDiv>
        <Select
          {...restFieldData}
          showSearch
          id={name}
          style={{ width: "100%" }}
          placeholder={placeholder}
          options={options}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          onChange={handleChange}
          classNames={{
            popup: { root: "tableSelectDropdown" },
          }}
          popupRender={popupRender}
          value={isLoading ? null : value || null}
          {...props}
        />
        <FormControlError
          id={`${name}-error`}
          errorMsg={getIn(touched, name) && getIn(errors, name)}
          className="tableInputField"
        />
      </StyledSelectTableDiv>
    </>
  );
};

export const FormControlTableSearchableSelect = ({
  name,
  context,
  options,
  handleOnChange = null,
  filterOption,
  ...props
}) => {
  const {
    formik: { getFieldProps, touched, errors, setFieldValue },
  } = useSelectContext(context);

  const { value, ...restFieldData } = getFieldProps(name);

  const handleCostTypeChange = (val, option) => {
    setFieldValue(name, val);
    if (handleOnChange) {
      handleOnChange(val, option);
    }
  };

  return (
    <StyledSelectTableDiv>
      <Select
        {...restFieldData}
        showSearch
        id={name}
        style={{ width: "100%" }}
        options={options}
        value={value}
        onChange={handleCostTypeChange}
        classNames={{
          popup: { root: "tableSelectDropdown" },
        }}
        filterOption={
          filterOption !== undefined
            ? filterOption
            : (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
        {...props}
      />
      <FormControlError
        id={`${name}-error`}
        errorMsg={getIn(touched, name) && getIn(errors, name)}
        className="tableInputField"
      />
    </StyledSelectTableDiv>
  );
};

const STRING_PARAMS = ["products"];
